import { ReactComponent as BillingLinkIcon } from 'assett/icons/billingLink.svg';
import { setSelectOne } from 'feature/tableSelectedItems/tableSelectedItemsSlice';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';
import {
	Button,
	Checkbox,
	IconButton,
	TableBody,
	TableCell,
	TableRow,
	Typography,
} from 'styles';
import DynamicTypography from 'styles/typography/DynamicTypography.styles';
import { IClaimBilling } from 'types/Billing';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import { getShortDate } from 'utils/utils';
import RebillingModal from './ReBillingModal.component';

interface IBillingTableBody {
	claimsBilling: IClaimBilling[];
}

const BillingTableBody = ({ claimsBilling }: IBillingTableBody) => {
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.DB_LABELS,
	});
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const { tabSelected } = useSelector((store: RootState) => store.billing);
	const { selectedItems, downloadAll } = useSelector(
		(store: RootState) => store.tableSelectedItems,
	);

	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();

	const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
	const [selectedClaimNumber, setSelectedClaimNumber] = useState<string>('');
	const [selectedUuid, setSelectedUuid] = useState<string>('');

	const onRebilling = (claim: IClaimBilling) => {
		setIsOpenModal(true);
		setSelectedClaimNumber(claim.claimNumber);
		setSelectedUuid(claim.claimUuid);
	};

	const { networks } = useSelector((store: RootState) => store.form);

	return claimsBilling.length > 0 ? (
		<TableBody>
			{claimsBilling.map((el, idx) => (
				<TableRow
					key={idx}
					sx={{
						'& > td': {

							padding: '5px 0px 5px 10px',
							'&:last-of-type': {
								minWidth: 'auto',
							},
							'&:first-of-type': {
								minWidth: 'auto',
							},
						},
					}}
				>
					<TableCell>
						<Checkbox
							label=""
							key={idx}
							sx={{ p: 0, ml: 0 }}
							checked={
								downloadAll
									? !selectedItems.find(uuid => el.claimUuid === uuid)
									: !!selectedItems.find(uuid => el.claimUuid === uuid)
							}
							onClick={() => dispatch(setSelectOne(el.claimUuid))}
						/>
					</TableCell>
					<TableCell>
						<Typography variant="body">{el.billingId || '-'}</Typography>
					</TableCell>
					<TableCell>
						<DynamicTypography variant="body" maxWidth={300}>
							{tDbLabels(el.billingType.label)}
						</DynamicTypography>
					</TableCell>
					<TableCell>
						{el.claimBillingStatus?.code === 'billed_mismatched' ||
						el.adminBillingStatus?.code === 'billed_mismatched' ? (
							<Button variant="linkLight" onClick={() => onRebilling(el)}>
								{tDbLabels(
									el.claimBillingStatus?.label || el.adminBillingStatus?.label || '',
								)}
							</Button>
						) : (
							<Typography variant="body">
								{tDbLabels(
									el.claimBillingStatus?.label || el.adminBillingStatus?.label || '',
								)}
							</Typography>
						)}
					</TableCell>
					<TableCell>
						<Typography variant="body">{el.carrier}</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">{el.client}</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">{el.group}</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">{el.claimNumber}</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">{el.carrierClaimId || '-'}</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">{getShortDate(el.claimDate)}</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">
							{getShortDate(el.billingDate) || '-'}
						</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">
							{getShortDate(el.settlementDate) || '-'}
						</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">{tDbLabels(el.claimStatus.label)}</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">
							{tDbLabels(
								`${
									el.inNetwork === null
										? ''
										: el.inNetwork
											? 'network_status.INN.short'
											: 'network_status.OON.short'
								}`,
							)}
						</Typography>
					</TableCell>
					<TableCell>
						<Typography variant="body">
							{networks.find(network => network.networkCode === el.networkCode)
								?.name || '-'}
						</Typography>
					</TableCell>
					<TableCell />
					<TableCell>
						{tabSelected === 'claims' && (
							<IconButton
								disabled={!el.carrierDocument || !el.carrierDocument.uuid}
								onClick={() =>
									navigate(`/view-settlement-letters/${el.carrierDocument.uuid}`)
								}
								sx={{
									cursor: 'pointer',
									opacity: !el.carrierDocument || !el.carrierDocument.uuid ? 0.2 : 1,
								}}
							>
								<BillingLinkIcon />
							</IconButton>
						)}
					</TableCell>
				</TableRow>
			))}
			<RebillingModal
				open={isOpenModal}
				setOpen={setIsOpenModal}
				claimNumber={selectedClaimNumber}
				claimUuid={selectedUuid}
			/>
		</TableBody>
	) : (
		<TableBody
			sx={{
				'& td': {
					paddingLeft: '0px!important',
					textAlign: 'left!important',
					backgroundColor: 'transparent!important',
				},
			}}
		>
			<TableRow>
				<TableCell colSpan={12}>
					<Typography variant="bodyLink" sx={{ width: '100%' }}>
						{tClientLabels('commons.noResultsFound')}
					</Typography>
				</TableCell>
			</TableRow>
		</TableBody>
	);
};

export default BillingTableBody;
